import Phaser from "phaser";
import type { TabViewContract, TabContentRect } from "@yjsgames/phaser-ui/tab-registry";
import { NineSliceButton, NINE_SLICE_BUTTON_EVENT } from "@yjsgames/phaser-ui";
import { FONT_FAMILY, SMALL_BUTTON_STYLE, TYPO } from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import type { ModuleGridController } from "../systems/ModuleGridController.js";
import type { PathResult } from "../modules/ModulePathFinder.js";
import type { BigNumber } from "@yjsgames/bignumber";
import { ModuleGridView, type BattleProcEvent } from "./ModuleGridView.js";
import type { ModuleRarity } from "../modules/ProjectileModule.js";
import { ModulePaletteView, type PaletteRow } from "./ModulePaletteView.js";
import { MarqueeText } from "./MarqueeText.js";
import {
  computeBattleTabLayoutInContentRect,
  type BattleTabLayout,
} from "./battleTabLayout.js";
import { BattleSynergyBadgeStrip, type BattleActiveSynergy } from "./BattleSynergyBadgeStrip.js";
import { SortFilterControl } from "./SortFilterControl.js";
import { applySort } from "./sortFilter.js";
import {
  DEFAULT_PALETTE_SORT,
  PALETTE_KEY_EXTRACTORS,
  PALETTE_SORT_KEYS,
  paletteTieBreak,
  type PaletteSortKey,
} from "./paletteSort.js";
import { MODULE_GRID, type ShellLayout } from "../data/balance.js";
import { getTextDirection, t } from "../i18n/i18n.js";
import { FORCE_LOW_SPEC_RENDER_ENABLED } from "../env/appFlavor.js";
import { clampPageIndex, resolveSpaceshipLanePage } from "./spaceshipLanePager.js";

export { battleActiveSynergyLabels, type BattleActiveSynergy } from "./BattleSynergyBadgeStrip.js";

export interface BattleTabViewCallbacks {
  readonly onPlace: (x: number, y: number, instanceId: string) => boolean | void;
  readonly onMove: (fromX: number, fromY: number, toX: number, toY: number) => void;
  /** 배치 모듈 탭 — 해당 instanceId의 정보 팝업 열기 (모양 순환은 폐기). */
  readonly onModuleInfo: (instanceId: string) => void;
  /** 시작점(런처) 탭 — 런처 정보 팝업 열기. */
  readonly onLauncherInfo: () => void;
  readonly onRemove: (x: number, y: number) => void;
  /** 모두 장착 해제 — 배치된 모든 모듈을 인벤토리로 회수. */
  readonly onUnequipAll: () => void;
  /** 추천 장착 — 보유 미배치 모듈을 높은 등급순으로 빈 칸에 최대한 채움. */
  readonly onRecommendEquip: () => void;
  readonly onUnlock: (x: number, y: number, visibleLaneIndex: number) => void;
  readonly unlockCostBig: () => BigNumber;
  /** definitionId → 표시명 (경로 라벨용). */
  readonly nameOf: (definitionId: string) => string;
  /** 발동 중 시너지 배지 클릭 — 시너지 정보 팝업 열기. */
  readonly onSynergyInfo?: (synergyId: string) => void;
}

/** synergyInstanceIds 미지정 시 기본값 — 매 updateAll 새 Set 생성 회피. */
const EMPTY_SYNERGY_SET: ReadonlySet<string> = new Set();
const EMPTY_RARITY_MAP: ReadonlyMap<string, ModuleRarity> = new Map();
const EMPTY_ACTIVE_SYNERGIES: readonly BattleActiveSynergy[] = [];

const SORT_W = 128;
/** 정렬/필터 드롭다운 트리거 높이 — sortRow에 얇게. */
const SORT_H = 12;
/** 액션 버튼(모두 해제/추천 장착) 메트릭 — sortRow 높이(16) 안에 들어가는 소형 버튼. */
const ACTION_BTN_W = 60;
const ACTION_BTN_H = 16;
const ACTION_BTN_GAP = 4;
const PAGE_BTN_W = 16;
const PAGE_LABEL_W = 30;
const PAGE_GAP = 3;
/** Phaser Text의 광학 baseline이 버튼보다 높아 보이는 현상을 보정하는 미세 오프셋. */
const PAGE_LABEL_Y_OFFSET = 3;
const GRID_BACKDROP_SHIP_SCALE = 0.56;
const GRID_BACKDROP_SHIP_ALPHA = 0.07;

function paletteRowsEqual(a: readonly PaletteRow[], b: readonly PaletteRow[]): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const x = a[i]!;
    const y = b[i]!;
    if (
      x.instanceId !== y.instanceId ||
      x.definitionId !== y.definitionId ||
      x.name !== y.name ||
      x.level !== y.level ||
      x.rarity !== y.rarity ||
      x.placed !== y.placed ||
      x.shapeId !== y.shapeId ||
      x.footprintId !== y.footprintId
    ) {
      return false;
    }
  }
  return true;
}

function sortStateKey(state: { readonly key: PaletteSortKey; readonly dir: string }): string {
  return `${state.key}:${state.dir}`;
}

function setVisibleActive<T extends Phaser.GameObjects.GameObject & { setVisible: (visible: boolean) => T }>(
  obj: T,
  visible: boolean,
): void {
  obj.setVisible(visible);
  (obj as Phaser.GameObjects.GameObject & { setActive?: (value: boolean) => unknown }).setActive?.(visible);
}

/**
 * 전투 탭 콘텐츠 — Pipe Mania식 모듈 조립 그리드 + 보유 모듈 팔레트 + 경로/스펙 요약 (지시문 §2·§3·§15).
 * 팔레트 타일을 그리드로 드래그하면 셀로 변환해 배치한다.
 *
 * 펼치기는 **셸 폴더탭(ShellFrame)이 전역 소유** — 게임뷰 축소·콘텐츠 오프셋(발사대/벽돌)은 셸/GameScene이,
 * 본 뷰는 `resize(rect)`로 셸이 넘긴 콘텐츠 영역에 그리드/팔레트/텍스트/정렬을 재배치만 한다(모든 탭 공용 계약).
 */
export class BattleTabView implements TabViewContract {
  /** 경로 연결 라벨 — 그리드가 꽉 차면 활성 모듈 나열이 길어져, 넘칠 때 마키+드래그 가로 스크롤. */
  private readonly pathText: MarqueeText;
  private readonly specText: Phaser.GameObjects.Text;
  private readonly gridBackdropShip: Phaser.GameObjects.Image;
  private readonly gridView: ModuleGridView;
  private readonly paletteView: ModulePaletteView;
  /** 보유 파츠 정렬 컨트롤(재사용 SortFilterControl). */
  private readonly sortControl: SortFilterControl<PaletteSortKey>;
  /** sortRow 좌측(RTL=우측) 소형 액션 버튼 — 모두 장착 해제 / 추천 장착. */
  private readonly unequipBtn: NineSliceButton;
  private readonly recommendBtn: NineSliceButton;
  private readonly prevPageBtn: NineSliceButton;
  private readonly nextPageBtn: NineSliceButton;
  private readonly pageLabel: Phaser.GameObjects.Text;
  /** 발동 시너지 칩 — spec/path와 분리된 전용 행에 모두 렌더, 넘치면 가로 스크롤. */
  private readonly synergyStrip: BattleSynergyBadgeStrip;
  private layout: BattleTabLayout;
  private visible = true;
  private pageIndex = 0;
  private pageCount = 1;
  // spec/path 텍스트는 t() 의존 — 언어 변경 시 마지막 상태로 재설정하기 위해 캐시.
  private lastSpecSummary = "";
  private lastGrid: ModuleGridController | null = null;
  private lastPath: PathResult | null = null;
  private lastRarityByInstanceId: ReadonlyMap<string, ModuleRarity> = EMPTY_RARITY_MAP;
  private lastSynergyInstanceIds: ReadonlySet<string> = EMPTY_SYNERGY_SET;
  private focusedSynergyInstanceIds: ReadonlySet<string> | null = null;
  private renderedSpecText = "";
  private renderedPathText = "";
  /** 정렬 미적용 최신 팔레트 rows — 정렬 변경 시 재정렬 소스. */
  private lastRows: readonly PaletteRow[] = [];
  private sortedRowsCache: readonly PaletteRow[] = [];
  private lastSortKey = "";
  constructor(
    scene: Phaser.Scene,
    _screenWidth: number,
    _layout: ShellLayout,
    initialRect: TabContentRect,
    private readonly cb: BattleTabViewCallbacks,
  ) {
    // 그리드/팔레트 DnD 공유 임계 — 씬 전역 정책. 기본 0이면 탭의 1~2px 미세 이동도 drag로
    // 처리돼 그리드 타일 탭(정보 팝업, onModuleInfo)이 스킵된다. 16px 이상 끌어야 드래그(이동)로 인식.
    scene.input.dragDistanceThreshold = 16;

    // 콘텐츠 rect는 ShellFrame이 탭 패널의 현재 접힘/펼침 영역만 반영해 넘긴다.
    const L = computeBattleTabLayoutInContentRect(initialRect);
    this.layout = L;

    this.pathText = new MarqueeText(scene, {
      x: L.pathText.x,
      y: L.pathText.y,
      width: L.pathText.width,
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.textSecondary,
      },
      depth: 3,
      rtl: getTextDirection() === "rtl",
      scrollbar: {
        trackColor: PALETTE_NUM.scrollTrack,
        thumbColor: PALETTE_NUM.scrollBar,
      },
    });
    this.pathText.setText(t("battle.noPath"));
    this.specText = scene.add
      .text(L.specText.x, L.specText.y, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.body,
        color: PALETTE.textSecondary,
      })
      .setDepth(3);
    this.specText.setFixedSize(Math.floor(L.pathText.width), 16);

    this.gridBackdropShip = scene.add
      .image(0, 0, "spaceship_large")
      .setName("battleGridSpaceshipBackdrop")
      .setOrigin(0.5)
      .setDepth(1)
      .setAlpha(GRID_BACKDROP_SHIP_ALPHA);
    this.positionGridBackdropShip(L);

    this.gridView = new ModuleGridView(scene, L.grid.originX, L.grid.originY, {
      onUnlock: cb.onUnlock,
      onMove: cb.onMove,
      onModuleInfo: cb.onModuleInfo,
      onLauncherInfo: cb.onLauncherInfo,
      onRemove: cb.onRemove,
      unlockCostBig: cb.unlockCostBig,
    });

    this.paletteView = new ModulePaletteView(
      scene,
      L.palette.x,
      L.palette.y,
      L.palette.width,
      L.palette.height,
      {
        onDrop: (instanceId, px, py) => {
          const cell = this.gridView.pointerToCell(px, py);
          return cell ? this.cb.onPlace(cell.x, cell.y, instanceId) === true : false;
        },
        onModuleInfo: cb.onModuleInfo,
      },
    );

    this.sortControl = new SortFilterControl<PaletteSortKey>(scene, 0, 0, {
      width: SORT_W,
      keys: PALETTE_SORT_KEYS,
      initial: DEFAULT_PALETTE_SORT,
      labelOf: (k) => t(`sort.${k}`),
      onChange: () => this.applyPaletteSort(),
      dir: getTextDirection(),
      triggerHeight: SORT_H,
    });

    this.unequipBtn = this.makeActionButton(scene, t("battle.unequipAll"), () =>
      this.cb.onUnequipAll(),
    );
    this.recommendBtn = this.makeActionButton(scene, t("battle.recommendEquip"), () =>
      this.cb.onRecommendEquip(),
    );
    this.prevPageBtn = this.makePagerButton(scene, "<", () => this.stepPage(-1));
    this.nextPageBtn = this.makePagerButton(scene, ">", () => this.stepPage(1));
    this.pageLabel = scene.add
      .text(0, 0, "1/1", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.micro,
        color: PALETTE.textSecondary,
        align: "center",
      })
      .setOrigin(0.5)
      .setDepth(3);
    this.pageLabel.setFixedSize(PAGE_LABEL_W, ACTION_BTN_H);

    this.synergyStrip = new BattleSynergyBadgeStrip(scene, L, cb.onSynergyInfo);

    this.positionControls(L);
    if (FORCE_LOW_SPEC_RENDER_ENABLED) this.setVisible(true);
  }

  /** 소형 액션 버튼 생성(SMALL_BUTTON_STYLE, sortRow 높이에 맞춤). 위치는 positionControls가 잡는다. */
  private makeActionButton(
    scene: Phaser.Scene,
    label: string,
    onClick: () => void,
  ): NineSliceButton {
    const btn = new NineSliceButton(scene, 0, 0, {
      style: SMALL_BUTTON_STYLE,
      label,
      widthOverride: ACTION_BTN_W,
      heightOverride: ACTION_BTN_H,
    });
    btn.setDepth(3);
    btn.on(NINE_SLICE_BUTTON_EVENT.Click, onClick);
    scene.add.existing(btn);
    return btn;
  }

  private makePagerButton(
    scene: Phaser.Scene,
    label: string,
    onClick: () => void,
  ): NineSliceButton {
    const btn = new NineSliceButton(scene, 0, 0, {
      style: SMALL_BUTTON_STYLE,
      label,
      widthOverride: PAGE_BTN_W,
      heightOverride: ACTION_BTN_H,
    });
    btn.setDepth(3);
    btn.on(NINE_SLICE_BUTTON_EVENT.Click, onClick);
    scene.add.existing(btn);
    return btn;
  }

  /**
   * 셸 펼치기/접기 — 셸이 넘긴 콘텐츠 rect에 맞춰 그리드/팔레트/텍스트/정렬을 재배치 (모든 탭 공용 계약).
   * rect는 셸이 현재 접힘/펼침 상태를 반영해 넘긴 최종 콘텐츠 영역이다.
   */
  resize(rect: TabContentRect): void {
    const L = computeBattleTabLayoutInContentRect(rect);
    this.layout = L;
    this.gridView.setOrigin(L.grid.originX, L.grid.originY);
    this.paletteView.resize(L.palette.x, L.palette.y, L.palette.width, L.palette.height);
    this.pathText.setViewport(L.pathText.x, L.pathText.y, L.pathText.width);
    this.specText.setPosition(L.specText.x, L.specText.y);
    this.specText.setFixedSize(Math.floor(L.pathText.width), 16);
    this.positionGridBackdropShip(L);
    this.positionControls(L);
    this.synergyStrip.resize(L);
  }

  private positionGridBackdropShip(L: BattleTabLayout): void {
    const size = Math.round(Math.min(L.grid.size, L.grid.height) * GRID_BACKDROP_SHIP_SCALE);
    this.gridBackdropShip
      .setPosition(L.grid.originX + L.grid.size / 2, L.grid.originY + L.grid.height / 2)
      .setDisplaySize(size, size);
  }

  setSpaceshipBackdropTexture(textureKey: string): void {
    if (this.gridBackdropShip.texture.key === textureKey) return;
    this.gridBackdropShip.setTexture(textureKey);
  }

  /** 정렬 컨트롤(전용 sortRow) 위치 — RTL이면 좌우 반전. 어떤 텍스트와도 겹치지 않는다(레이아웃 정적검증 대상). */
  private positionControls(L: BattleTabLayout): void {
    const rtl = getTextDirection() === "rtl";
    const sortCx = rtl ? L.sort.x + SORT_W / 2 : L.sort.x + L.sort.width - SORT_W / 2;
    const cy = L.sort.y + L.sort.height / 2;
    this.sortControl.setPosition(sortCx, cy);
    // 액션 버튼은 sort 드롭다운 반대편(LTR=좌측, RTL=우측)에 가로로 — 드롭다운과 x범위가 겹치지 않게.
    const step = ACTION_BTN_W + ACTION_BTN_GAP;
    if (rtl) {
      const x1 = L.sort.x + L.sort.width - ACTION_BTN_W / 2; // 우측 끝부터 좌로
      this.unequipBtn.setPosition(x1, cy);
      this.recommendBtn.setPosition(x1 - step, cy);
    } else {
      const x1 = L.sort.x + ACTION_BTN_W / 2; // 좌측 끝부터 우로
      this.unequipBtn.setPosition(x1, cy);
    this.recommendBtn.setPosition(x1 + step, cy);
    }
    this.positionGridPager(L);
    this.pathText.setViewport(L.pathText.x, L.pathText.y, L.pathText.width);
  }

  private positionGridPager(L: BattleTabLayout): void {
    const cy = L.gridPager.y + L.gridPager.height / 2;
    const pagerW = PAGE_BTN_W * 2 + PAGE_LABEL_W + PAGE_GAP * 2;
    const pagerLeft = L.gridPager.x + L.gridPager.width / 2 - pagerW / 2;
    this.prevPageBtn.setPosition(pagerLeft + PAGE_BTN_W / 2, cy);
    this.pageLabel.setPosition(pagerLeft + PAGE_BTN_W + PAGE_GAP + PAGE_LABEL_W / 2, cy + PAGE_LABEL_Y_OFFSET);
    this.nextPageBtn.setPosition(pagerLeft + PAGE_BTN_W + PAGE_GAP + PAGE_LABEL_W + PAGE_GAP + PAGE_BTN_W / 2, cy);
  }

  /** 정렬 변경/데이터 갱신 시 lastRows를 현재 정렬로 재정렬해 팔레트에 반영. */
  private applyPaletteSort(): void {
    this.lastSortKey = sortStateKey(this.sortControl.current);
    this.sortedRowsCache = this.sortedRows();
    this.paletteView.update(this.sortedRowsCache);
  }

  private sortedRows(): PaletteRow[] {
    return applySort(this.lastRows, this.sortControl.current, PALETTE_KEY_EXTRACTORS, paletteTieBreak);
  }

  updateAll(
    grid: ModuleGridController,
    path: PathResult,
    rows: readonly PaletteRow[],
    specSummary: string,
    synergyInstanceIds: ReadonlySet<string> = EMPTY_SYNERGY_SET,
    rarityByInstanceId: ReadonlyMap<string, ModuleRarity> = EMPTY_RARITY_MAP,
    activeSynergies: readonly BattleActiveSynergy[] = EMPTY_ACTIVE_SYNERGIES,
    spaceshipLargeAssetKey = "spaceship_large",
  ): void {
    this.setSpaceshipBackdropTexture(spaceshipLargeAssetKey);
    this.pageCount = Math.max(1, ((grid as unknown as { lanes?: readonly unknown[] }).lanes?.length ?? 1));
    this.pageIndex = clampPageIndex(this.pageIndex, this.pageCount);
    this.updatePager();
    const nextSortKey = sortStateKey(this.sortControl.current);
    const paletteChanged =
      nextSortKey !== this.lastSortKey || !paletteRowsEqual(rows, this.lastRows);
    if (paletteChanged) {
      this.lastRows = rows;
      this.lastSortKey = nextSortKey;
      this.sortedRowsCache = this.sortedRows();
    }
    if (paletteChanged) this.paletteView.update(this.sortedRowsCache);
    this.lastGrid = grid;
    this.lastSpecSummary = specSummary;
    this.lastPath = path;
    this.lastRarityByInstanceId = rarityByInstanceId;
    this.lastSynergyInstanceIds = synergyInstanceIds;
    if (this.focusedSynergyInstanceIds && !this.focusedSynergyStillActive(synergyInstanceIds)) {
      this.focusedSynergyInstanceIds = null;
    }
    this.renderSpecAndPath();
    this.synergyStrip.update(activeSynergies);
    this.renderGrid(grid, path);
  }

  private updatePager(): void {
    const visible = this.visible && this.pageCount > 1;
    this.pageLabel.setText(`${this.pageIndex + 1}/${this.pageCount}`);
    setVisibleActive(this.pageLabel, visible);
    this.prevPageBtn.setVisible(visible);
    this.nextPageBtn.setVisible(visible);
  }

  private stepPage(delta: number): void {
    if (this.pageCount <= 1) return;
    const next = clampPageIndex(this.pageIndex + delta, this.pageCount);
    if (next === this.pageIndex) return;
    this.pageIndex = next;
    this.updatePager();
    const grid = this.lastGrid;
    const path = this.lastPath;
    if (grid && path) this.renderGrid(grid, path);
  }

  /** spec/path 텍스트를 마지막 상태로 재설정 — t() 의존이라 언어 변경 시에도 재호출(객체 재생성 0). */
  private renderSpecAndPath(): void {
    const specText = t("battle.spec", { summary: this.lastSpecSummary });
    if (specText !== this.renderedSpecText) {
      this.renderedSpecText = specText;
      this.specText.setText(specText);
    }
    const path = this.lastPath;
    // 지시문 §10: 3상태 — 미연결 / 연결+모듈없음 / 연결+모듈.
    const pathText =
      (!path || !path.connected
        ? t("battle.noPath")
        : path.activeDefinitionIds.length === 0
          ? t("battle.basicPath")
          : t("battle.pathConnected", {
              path: path.activeDefinitionIds.map((id) => this.cb.nameOf(id)).join(" → "),
            }));
    if (pathText !== this.renderedPathText) {
      this.renderedPathText = pathText;
      this.pathText.setText(pathText);
    }
  }

  highlightSynergyPipes(instanceIds: ReadonlySet<string>): void {
    this.focusedSynergyInstanceIds = instanceIds;
    const grid = this.lastGrid;
    const path = this.lastPath;
    if (grid && path) this.renderGrid(grid, path);
  }

  clearFocusedSynergyPipes(): void {
    this.focusedSynergyInstanceIds = null;
    const grid = this.lastGrid;
    const path = this.lastPath;
    if (grid && path) this.renderGrid(grid, path);
  }

  private focusedSynergyStillActive(activeInstanceIds: ReadonlySet<string>): boolean {
    const focused = this.focusedSynergyInstanceIds;
    if (!focused || focused.size === 0) return false;
    for (const id of focused) {
      if (!activeInstanceIds.has(id)) return false;
    }
    return true;
  }

  private renderGrid(grid: ModuleGridController, path: PathResult): void {
    this.gridView.setOrigin(...this.centeredGridOrigin(grid));
    this.gridView.update(
      grid,
      path,
      this.focusedSynergyInstanceIds ?? this.lastSynergyInstanceIds,
      this.lastRarityByInstanceId,
      this.focusedSynergyInstanceIds !== null,
      this.pageIndex,
    );
  }

  private centeredGridOrigin(grid: ModuleGridController): [number, number] {
    const step = MODULE_GRID.cellSize + MODULE_GRID.cellGap;
    const width = grid.width * step - MODULE_GRID.cellGap;
    const lanePage = resolveSpaceshipLanePage(
      grid.height,
      (grid as unknown as { lanes?: readonly import("../models/ModuleGrid.js").ModuleGridLane[] }).lanes,
      this.pageIndex,
      MODULE_GRID.visibleRows,
    );
    const height = lanePage.visibleRows * step - MODULE_GRID.cellGap;
    const x = this.layout.grid.originX + Math.max(0, (this.layout.grid.size - width) / 2);
    const y = this.layout.grid.originY + Math.max(0, (this.layout.grid.height - height) / 2);
    return [x, y];
  }

  /** 발동 연출 — 발동한 instanceId 셀 + (startFired면) 시작 파이프에 "발동!" 텍스트 + 펄스(GameScene.fire 호출). */
  showProc(procs: readonly BattleProcEvent[], startFired = false): void {
    this.gridView.showProc(procs, startFired);
  }

  /** 언어 변경 — 팔레트 라벨 + 그리드 t()텍스트 + spec/path + 정렬 라벨 갱신. */
  applyLanguage(): void {
    this.paletteView.applyLanguage();
    this.gridView.applyLanguage();
    this.sortControl.applyLanguage();
    this.unequipBtn.setLabel(t("battle.unequipAll"));
    this.recommendBtn.setLabel(t("battle.recommendEquip"));
    this.renderSpecAndPath();
    this.updatePager();
    this.synergyStrip.applyLanguage();
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    const richUiVisible = visible;
    this.pathText.setVisible(richUiVisible);
    setVisibleActive(this.specText, richUiVisible);
    setVisibleActive(this.gridBackdropShip, visible);
    this.gridView.setVisible(visible);
    this.paletteView.setVisible(visible);
    this.sortControl.setVisible(richUiVisible);
    this.unequipBtn.setVisible(richUiVisible);
    this.recommendBtn.setVisible(richUiVisible);
    this.pageLabel.setVisible(richUiVisible && this.pageCount > 1);
    this.prevPageBtn.setVisible(richUiVisible && this.pageCount > 1);
    this.nextPageBtn.setVisible(richUiVisible && this.pageCount > 1);
    this.synergyStrip.setVisible(richUiVisible);
    // 펼침 상태는 셸(GameScene)이 전역 소유 — 탭 enter 시 GameScene이 tabs.resize로 현재 상태 재적용.
  }
}
